/**
 * The status circle as one shape with six poses, so a status change can morph
 * between poses instead of swapping icons (Linear's behaviour): the pie sweeps
 * clockwise to its new amount, the ink crossfades, and on the way to Done the
 * disc fills, the check draws in and the circle pops. Going back reverses it.
 *
 * Pure data and maths only; `StatusIcon` (components/tasks/task-glyphs.tsx)
 * renders a pose and plays the tweens with the Web Animations API.
 */

import type { TaskState } from "./task-boards"

/** Every layer of the circle, each 0..1 (pie is the filled fraction). */
export type StatusPose = {
  ring: number
  dashed: number
  pie: number
  disc: number
  check: number
  cross: number
  alert: number
}

export type StatusLayer = keyof StatusPose

const OFF: StatusPose = { ring: 0, dashed: 0, pie: 0, disc: 0, check: 0, cross: 0, alert: 0 }

export const STATUS_POSES: Record<TaskState, StatusPose> = {
  triage: { ...OFF, ring: 1, alert: 1 },
  backlog: { ...OFF, dashed: 1 },
  todo: { ...OFF, ring: 1 },
  in_progress: { ...OFF, ring: 1, pie: 0.5 },
  done: { ...OFF, ring: 1, pie: 1, disc: 1, check: 1 },
  canceled: { ...OFF, ring: 1, disc: 1, cross: 1 },
}

/** Ink per status as [light, dark], the a11 step of each tone (resend-styles). */
export const STATUS_INK: Record<TaskState, readonly [string, string]> = {
  triage: ["#d9920b", "#e9a93a"],
  backlog: ["#989898", "#6e7679"],
  todo: ["#7e7e7e", "#8f8f8f"],
  in_progress: ["#d9920b", "#e9a93a"],
  done: ["#2f8f57", "#4fbf7d"],
  canceled: ["#989898", "#6e7679"],
}

// Geometry in the 16-unit viewBox. The pie is a stroke as wide as the circle
// it is drawn on, so a dash of fraction f is a pie slice of f; a full pie is a
// disc of radius 4.4, which the r=7 disc then grows out of.
export const PIE_RADIUS = 2.2
export const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS
export const DISC_RADIUS = 7
const DISC_REST_SCALE = (PIE_RADIUS * 2) / DISC_RADIUS

/** stroke-dashoffset that shows `fraction` of the pie. */
export function pieOffset(fraction: number): number {
  return PIE_CIRCUMFERENCE * (1 - fraction)
}

/** The disc grows out of the full pie rather than from nothing. */
export function discScale(disc: number): number {
  return DISC_REST_SCALE + (1 - DISC_REST_SCALE) * disc
}

/** How the circle sits at rest in a pose: the CSS values each layer renders. */
export function poseStyle(layer: StatusLayer, value: number): { opacity?: number; strokeDashoffset?: number; transform?: string } {
  switch (layer) {
    case "pie":
      return { strokeDashoffset: pieOffset(value) }
    case "disc":
      return { opacity: value, transform: `scale(${discScale(value)})` }
    case "check":
    case "cross":
      // Both marks use pathLength=1, so the offset is the undrawn share. The
      // fade hides the round cap a fully undrawn stroke still paints.
      return { strokeDashoffset: 1 - value, opacity: value > 0 ? 1 : 0 }
    default:
      return { opacity: value }
  }
}

export type StatusTween = {
  layer: StatusLayer | "svg"
  keyframes: Keyframe[]
  delay: number
  duration: number
  easing: string
}

export const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"

/**
 * The tweens for one change. Filling (toward Done or Canceled) runs pie, then
 * disc, then mark; emptying runs the same three phases backwards. A change
 * that never touches the disc (todo to in progress) is one ~200ms sweep. The
 * whole thing stays under 320ms.
 *
 * `reducedMotion` drops every sweep, draw and pop and keeps a short crossfade.
 */
export function statusMorphTweens(
  from: TaskState,
  to: TaskState,
  { reducedMotion = false, dark = false }: { reducedMotion?: boolean; dark?: boolean } = {},
): StatusTween[] {
  if (from === to) return []
  const a = STATUS_POSES[from]
  const b = STATUS_POSES[to]
  const inkFrom = STATUS_INK[from][dark ? 1 : 0]
  const inkTo = STATUS_INK[to][dark ? 1 : 0]

  if (reducedMotion) {
    return [{
      layer: "svg",
      keyframes: [{ opacity: 0.35, color: inkFrom }, { opacity: 1, color: inkTo }],
      delay: 0,
      duration: 150,
      easing: "linear",
    }]
  }

  const filling = b.disc > a.disc
  const emptying = b.disc < a.disc
  // [delay, duration] per phase.
  const phases = filling
    ? { sweep: [0, 160], disc: [100, 120], mark: [160, 140] }
    : emptying
      ? { mark: [0, 100], disc: [60, 120], sweep: [120, 160] }
      : { sweep: [0, 200], disc: [0, 200], mark: [0, 200] }
  const phaseOf: Record<StatusLayer, keyof typeof phases> = {
    ring: "sweep",
    dashed: "sweep",
    pie: "sweep",
    alert: "sweep",
    disc: "disc",
    check: "mark",
    cross: "mark",
  }

  const tweens: StatusTween[] = []
  for (const layer of Object.keys(a) as StatusLayer[]) {
    if (a[layer] === b[layer]) continue
    const [delay, duration] = phases[phaseOf[layer]]
    tweens.push({
      layer,
      keyframes: [poseStyle(layer, a[layer]), poseStyle(layer, b[layer])],
      delay,
      duration,
      easing: EASE_OUT,
    })
  }

  const total = Math.max(200, ...tweens.map((t) => t.delay + t.duration))
  tweens.push({
    layer: "svg",
    keyframes: [{ color: inkFrom }, { color: inkTo }],
    delay: 0,
    duration: Math.min(total, 220),
    easing: "linear",
  })
  if (filling) {
    const [delay] = phases.mark
    tweens.push({
      layer: "svg",
      // Up fast, settle slower: a small spring without overshooting below 1.
      keyframes: [
        { transform: "scale(1)", easing: "cubic-bezier(0.2, 0, 0, 1)" },
        { transform: "scale(1.18)", offset: 0.35, easing: "cubic-bezier(0.3, 0, 0.2, 1)" },
        { transform: "scale(1)" },
      ],
      delay,
      duration: 150,
      easing: "linear",
    })
  }
  return tweens
}
